// Angular
import { Directive, HostListener, input, inject, ElementRef, OnDestroy, OnInit } from '@angular/core';
// Angular Material
import { MatTooltip } from '@angular/material/tooltip';
// RxJS
import { Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
    selector: '[copiedToClipboardTooltip]',
    hostDirectives: [MatTooltip],
})
export class CopiedToClipboardTooltipDirective implements OnInit, OnDestroy {

    // Inject
    private readonly hostElement = inject(ElementRef<HTMLElement>);
    private readonly tooltip = inject(MatTooltip);

    // RxJS Subject to handle unsubscription
    private readonly destroy$ = new Subject<void>();

    // Directive Inputs 
    readonly textToCopy = input.required<string | number>();
    readonly successMessage = input.required<string>();
    readonly errorMessage = input<string>('Failed to copy!');


    /**
     * Initilization
     * @memberOf CopiedToClipboardTooltipDirective
     */
    ngOnInit(): void {
        this.tooltip.disabled = true;
    }

    /**
     * Implements OnDestroy to automatically unsubscribe when the directive is destroyed.
     * @memberOf CopiedToClipboardTooltipDirective
     */
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Handles the double-click event by copying the entire input text.
     */
    @HostListener('dblclick')
    onDoubleClick(): void {
        this._copyTextAndShowFeedback(this.textToCopy());
    }

    /**
     * Handles the mouseup event by copying only the user-selected text.
     */
    @HostListener('mouseup')
    onMouseUp(): void {
        try {
            const selection = window.getSelection();
            const selectedText = selection?.toString().trim();

            if (selectedText && this.hostElement.nativeElement.contains(selection.anchorNode)) {
                this._copyTextAndShowFeedback(selectedText);
            }
        } catch (err) {
            console.log(err);
        }
    }

    /**
     * Central helper method to perform the copy action and show feedback.
     * @param {(string | number)} text 
     * @returns {Promise<boolean>} 
     * @memberOf CopiedToClipboardTooltipDirective
     */
    private async _copyTextAndShowFeedback(text: string | number): Promise<boolean> {
        if (!text) return false;
        try {
            await navigator.clipboard.writeText(`${text}`);
            this._showOneTimeTooltip(this.successMessage());
            return true;
        } catch (err) {
            console.error('Failed to copy text:', err);
            this._showOneTimeTooltip(this.errorMessage());
            return false;
        }
    }

    /**
     * Internal helper to show tooltip only once and disable it again.
     * @private
     * @param {string} message 
     * @memberOf CopiedToClipboardTooltipDirective
     */
    private _showOneTimeTooltip(message: string): void {
        this.tooltip.message = message;
        this.tooltip.disabled = false; // temporarily enable
        this.tooltip.show();

        timer(1200).pipe(
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.tooltip.hide();
            this.tooltip.disabled = true;
        });
    }
}